/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Injectable, Injector } from "@angular/core";
import { EventBusService, Logger, TitanMsg } from "@html-core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { NetWorkMonitorType } from "../../../shared/desktop/wmks/net-watch/network-monitor-type";
import { NetworkMonitorService } from "../../../shared/desktop/wmks/net-watch/network-monitor.service";
import { TitanTokenService } from "../../../titan/services/titan-token.service";
import { BlastCloseEvent } from "../../../shared/desktop/wmks/wmks-error-handler";
import { clientUtil } from "@html-core";

@Injectable({
   providedIn: "root"
})
export class RedirectSettingsRecoveryService {
   private networkMonitor: NetworkMonitorService = null;
   private titanTokenService: TitanTokenService = null;
   private networkConnected: boolean = true;
   private heartBeatStatus: number = 0;
   private recoveryCallbackFunc: (msg: any) => void = null;
   private recoveringSessions = new Set<string>();
   private destroy$ = new Subject<void>();

   constructor(
      private injector: Injector,
      private eventBusService: EventBusService
   ) {
      this.networkMonitor = new NetworkMonitorService(injector);
      this.titanTokenService = injector.get(TitanTokenService);

      this.networkMonitor
         .monitor()
         .pipe(takeUntil(this.destroy$))
         .subscribe((currentState) => {
            this.heartBeatStatus = currentState.heatBeatStatus;
            if (currentState.hasNetworkConnection && currentState.hasInternetAccess) {
               this.networkConnected = true;
               Logger.debug("blast session ON_LINE");
            } else {
               this.networkConnected = false;
               Logger.info("blast session OFF_LINE");
               this.networkMonitor.updateHeartBeatUrl();
               if (!clientUtil.isTitanClient()) {
                  Logger.info("Stopping network monitor as its not a Titan Client");
                  this.networkMonitor.stop();
               }
            }
         });

      this.eventBusService
         .listen(TitanMsg.ProtocolRecoveryResponse.MSG_TYPE)
         .pipe(takeUntil(this.destroy$))
         .subscribe((msg: TitanMsg.ProtocolRecoveryResponse) => {
            this.recoveryCallbackFunc(msg);
            this.recoveringSessions.delete(msg.id);
         });
   }

   public init = (recoveryCallback: (msg: any) => void) => {
      this.recoveryCallbackFunc = recoveryCallback;
   };

   public recoverRedirectSettings = (sessionKey, message: TitanMsg.ChromeProtocolRecoveryRequest) => {
      if (this.recoveringSessions.has(sessionKey)) {
         Logger.info(`Recovery for session ${sessionKey} is already in progress. Ignoring request.`);
         return;
      }
      if (this.canDoReconnectForUAG(message.errorCode, message.closeReason)) {
         Logger.info("Titan blast session do UAG short Term Reconnection for sessionKey : " + sessionKey);
         this.recoveringSessions.add(sessionKey);
         this.sendProtocolRedirectionMsg(message);
      }
   };

   private sendProtocolRedirectionMsg = (message: TitanMsg.ChromeProtocolRecoveryRequest) => {
      const msg: TitanMsg.ProtocolRecoveryRequest = new TitanMsg.ProtocolRecoveryRequest();
      msg.id = message.id;
      msg.isApplication = message.isApplication;
      msg.protocolToken = message.protocolToken;
      msg.brokerUrl = message.brokerUrl;
      msg.uagUrl = message.uagUrl;
      msg.tunnelToken = message.tunnelToken;
      msg.dspecId = message.dspecId;
      this.eventBusService.dispatch(msg);
   };

   private canDoReconnectForUAG = (code: number, reason: number) => {
      if (!this.titanTokenService.getAccessToken()) {
         return false;
      }

      if (this.networkMonitor.getNetworkMonitorType() === NetWorkMonitorType.GLOBAL_MONITOR) {
         if (code === BlastCloseEvent.ABNORMAL_CLOSURE && this.networkConnected) {
            Logger.info("do UAG reconnection with GLOBAL_MONITOR");
            return true;
         }
      }

      if (code === BlastCloseEvent.ABNORMAL_CLOSURE && this.networkConnected) {
         if (this.heartBeatStatus === 404) {
            Logger.info("do UAG reconnection with UAG_MONITOR");
            return true;
         }
      }
      return false;
   };

   ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
